import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { map, Observable } from 'rxjs';
import { Beneficiary } from '../../../core/api/models/beneficiary';
import { ExpatPricing } from '../../../core/api/models';
import {
  CurrencySymbol,
  PricingPlan
} from '../../../shared/helpers/client.utils';
import { CurrencyFormatPipe } from '../../../shared/pipes/currency-format.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { LanguageStore } from '../../../shared/services/language.store';
import { PriceAndFrequencyViewModel } from '../../subscription-recap/subscription-recap.component';
import { ProjectStateService } from '../../../shared/services/state/project-state.service';
import { ReferenceStateService } from '../../../shared/services/state/reference-state.service';

@Component({
  selector: 'app-acs-tarifs-resume',
  imports: [
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    DatePipe,
    TranslatePipe,
    AsyncPipe,
    CurrencyFormatPipe,
    RouterLink
  ],
  templateUrl: './tarifs-resume.component.html',
  styles: `
    p,
    h4 {
      font-size: 1rem;
    }

    .amount {
      background-color: #f5c943;
      padding: 8px 16px;
      border-radius: 8px;
    }

    .gap-s {
      gap: 16px;
    }

    .fixed-bottom {
      position: fixed;
      bottom: 8px;
      background-color: #f2f4f3;
    }

    .scroll-panel {
      height: 67vh;
      margin-bottom: 70px;
      overflow-y: scroll;
      overflow-x: hidden;
      padding-bottom: 48px !important;
    }

    @media only screen and (min-width: 1224px) {
      .scroll-panel {
        height: fit-content;
        overflow-y: hidden;
        margin-bottom: 0;
        padding-bottom: 0 !important;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TarifsResumeComponent {
  readonly #langageStore = inject(LanguageStore);
  readonly projectStateService: ProjectStateService =
    inject(ProjectStateService);
  readonly referenceStateService: ReferenceStateService = inject(
    ReferenceStateService
  );
  protected pricingInfos = this.projectStateService.getPricingInformations()!;
  protected PricingPlan = PricingPlan;
  protected plan =
    this.PricingPlan[
      this.pricingInfos.expat_project_plan as keyof typeof PricingPlan
    ];
  protected deductible = this.pricingInfos.expat_project_deductible
    ? this.pricingInfos.expat_project_deductible
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    : '0';
  public CurrencySymbol = CurrencySymbol;
  public $price: WritableSignal<PriceAndFrequencyViewModel> = signal({
    price: 0,
    frequency: ''
  });

  public $expatCountry: Signal<string | undefined> = computed(() => {
    const country = this.referenceStateService
      .selectExpatriationCountries()()
      ?.find((c) => c.alpha3 === this.pricingInfos.expat_project_country);
    return country ? country.label : undefined;

    // const currentLanguage = this.#langageStore.currentLanguageCode();
    // return this.referenceStateService.selectAcsProjectDataModel().pipe(
    //   map((res) => {
    //     if (!res.expatriation_countries) {
    //       return [];
    //     }
    //     return currentLanguage === 'fr'
    //       ? res.expatriation_countries?.fr
    //       : res.expatriation_countries?.en;
    //   }),
    //   map((res) => {
    //     return res.find(
    //       (c) => c.alpha3 === this.pricingInfos.expat_project_country
    //     )!.label;
    //   })
    // );
  });

  public $nationalitySubscriber: Signal<string | undefined> = computed(() => {
    const country = this.referenceStateService
      .selectNationalityCountries()()
      ?.find((c) => c.alpha3 === this.pricingInfos.expat_project_nationality);
    return country ? country.label : undefined;
  });

  public $beneficiariesSummary: Signal<Beneficiary[]> = computed(() => {
    const beneficiaries = this.projectStateService.selectProjectBeneficiaries() ?? [];
    const subscriber = beneficiaries.find((b) => b.category === 'Souscripteur');
    if (!subscriber) {
      return beneficiaries;
    }

    return [
      subscriber,
      ...beneficiaries.filter((beneficiary) => beneficiary !== subscriber)
    ];
  });

  public $secondExpatCountry: Signal<Observable<string>> = computed(() => {
    const currentLanguage = this.#langageStore.currentLanguageCode();
    return this.referenceStateService.selectAcsProjectDataModel().pipe(
      map((res) => {
        if (!res.expatriation_countries) {
          return [];
        }
        return currentLanguage === 'fr'
          ? res.expatriation_countries?.fr
          : res.expatriation_countries?.en;
      }),
      map((res) => {
        return (
          res.find(
            (c) =>
              c.alpha3 === this.pricingInfos.expat_project_additional_country
          )?.label ?? ''
        );
      })
    );
  });

  public $expatPricing: Signal<ExpatPricing> =
    this.projectStateService.selectExpatPricing();

  public $coverageZone: Signal<string | undefined> = computed(() => {
    const currentLanguage = this.#langageStore.currentLanguageCode();
    return currentLanguage === 'fr'
      ? this.$expatPricing().coverage_zone?.fr
      : this.$expatPricing().coverage_zone?.en;
  });

  constructor(
    private _bottomSheetRef: MatBottomSheetRef<TarifsResumeComponent>
  ) {
    const periodicity_pricing = this.$expatPricing().periodicity_pricing;
    switch (
      this.projectStateService.getPricingInformations()
        ?.expat_project_periodicity_choice
    ) {
      case 1:
        this.$price.set({
          price: periodicity_pricing?.annual,
          frequency: 'subscription.resume.frequency.yearly'
        });
        break;
      case 2:
        this.$price.set({
          price: periodicity_pricing?.semi_annual,
          frequency: 'subscription.resume.frequency.sixmonths'
        });

        break;
      case 3:
        this.$price.set({
          price: periodicity_pricing?.quarterly,
          frequency: 'subscription.resume.frequency.threemonths'
        });
        break;
      case 4:
        this.$price.set({
          price: periodicity_pricing?.monthly,
          frequency: 'subscription.resume.frequency.monthly'
        });
        break;
    }
  }

  public dissmiss() {
    this._bottomSheetRef.dismiss();
  }
}
